import { lazy, Suspense, useMemo, useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import PortfolioTimeframeSelect from "@/v2-backoffice-client/portfolio/PortfolioTimeframeSelect"
import { formatMunicipalityName } from "@/v2-backoffice-client/usage/usage.constants"
import {
  GEO_SIGNIN_COLOR,
  GEO_WORK_COLOR,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  usePortfolioTimeframe,
  type PortfolioFilters,
} from "@/v2-backoffice-client/portfolio/portfolio.filters"
import { usePortfolioGeoQuery } from "@/v2-backoffice-client/portfolio/portfolio.geo.query"
import {
  deriveFleetGeoAreas,
  matchSignInLocations,
} from "@/v2-backoffice-client/portfolio/portfolio.geo.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"
// The type-only import is erased at build time; the VALUE import happens
// through lazy() below. Keeping @arcgis/core out of the statically-reachable
// graph is load-bearing: bundled statically it lands in the initial chunk,
// where its internal circular imports evaluate out of order and crash the
// whole app ("Class extends value undefined" in PortalItemResource.js).
import type { PortfolioGeoPlace } from "@/v2-backoffice-client/portfolio/PortfolioGeoMap"

const PortfolioGeoMap = lazy(
  () => import("@/v2-backoffice-client/portfolio/PortfolioGeoMap"),
)

export type PortfolioGeoSectionProps = ComponentProps<"section"> & {
  /** Already scoped by the global filters — the work layer derives from these. */
  customers: PortfolioCustomerSummary[]
  /** Global filters, forwarded to the sign-in rollup server-side. */
  filters: PortfolioFilters
  /**
   * Resolved include-ids for the server query — the facet filters (churn,
   * activity) resolve to explicit ids client-side, so this can differ from
   * `filters.customerIds`. Null = no include filter.
   */
  customerIds: string[] | null
}

const SIDE_LIST_LIMIT = 12

// The two geographies on one map: where the portfolio's tracked WORK
// happens (usage municipalities, green) and where the users SIGN IN from
// (geoip cities resolved to municipalities, teal). Work concentration is
// where market reports and prospecting pitches should anchor; sign-in
// origins say where the customers themselves sit.
function PortfolioGeoSection(props: PortfolioGeoSectionProps) {
  const { customers, filters, customerIds, className, ...sectionProps } = props

  // The sign-in layer re-windows on demand (raw events, aggregated
  // server-side), so it carries its own timeframe — one of only two on the
  // whole Portfolio. The work-areas layer stays 28d (nightly profiles).
  const timeframe = usePortfolioTimeframe("pf-geo-window")
  const timeWindow = timeframe.window

  const { data, error, loading } = usePortfolioGeoQuery({
    variables: {
      from: timeWindow.from.toISOString(),
      to: timeWindow.to ? timeWindow.to.toISOString() : null,
      customerIds,
      excludeCustomerIds:
        filters.excludeCustomerIds.length > 0
          ? filters.excludeCustomerIds
          : null,
      excludeUserIds:
        filters.excludeUserIds.length > 0 ? filters.excludeUserIds : null,
    },
  })
  const municipalities = useMemo(
    () => data?.getMunicipalityCentroids ?? [],
    [data?.getMunicipalityCentroids],
  )
  const signInCounts = useMemo(
    () => data?.getSignInLocationCounts ?? [],
    [data?.getSignInLocationCounts],
  )

  const fleetGeo = useMemo(() => deriveFleetGeoAreas(customers), [customers])
  const centroidByCode = useMemo(
    () =>
      new Map(
        municipalities.map((municipality) => [municipality.Code, municipality]),
      ),
    [municipalities],
  )

  const workPlaces = useMemo<PortfolioGeoPlace[]>(
    () =>
      fleetGeo.flatMap((area) => {
        const centroid = centroidByCode.get(area.code)
        if (!centroid) return []
        return [
          {
            key: area.code,
            // Registry names arrive UPPERCASE ("ASKER"); popups read better
            // title-cased.
            name: formatMunicipalityName(centroid.Name),
            value: area.count,
            detail: `${formatNumber(area.count)} located actions · last 28 d`,
            lat: centroid.Lat,
            long: centroid.Long,
          },
        ]
      }),
    [fleetGeo, centroidByCode],
  )

  const signInGeo = useMemo(
    () => matchSignInLocations(signInCounts, municipalities),
    [signInCounts, municipalities],
  )
  const signInPlaces = useMemo<PortfolioGeoPlace[]>(
    () =>
      signInGeo.matched.map((place) => ({
        key: place.code,
        name: formatMunicipalityName(place.name),
        value: place.signIns,
        detail: `${formatNumber(place.signIns)} sign-ins · ${formatNumber(place.users)} users in the selected timeframe`,
        lat: place.lat,
        long: place.long,
      })),
    [signInGeo],
  )

  const [showWork, setShowWork] = useState(true)
  const [showSignIns, setShowSignIns] = useState(true)
  const [search, setSearch] = useState("")

  const searchTerm = search.trim().toLowerCase()
  const workRows = fleetGeo
    .map((area) => ({
      key: area.code,
      // Both sources arrive UPPERCASE; title-case for display.
      label: formatMunicipalityName(
        centroidByCode.get(area.code)?.Name ?? area.name,
      ),
      value: area.count,
    }))
    .filter(
      (row) =>
        searchTerm === "" || row.label.toLowerCase().includes(searchTerm),
    )
  const signInRows = signInCounts
    .map((row) => ({
      key: row.Location,
      label: row.Location,
      value: row.SignInCount,
    }))
    .filter(
      (row) =>
        searchTerm === "" || row.label.toLowerCase().includes(searchTerm),
    )

  // Unsearched views stay top-N; a search looks across EVERYTHING.
  const topWork =
    searchTerm === "" ? workRows.slice(0, SIDE_LIST_LIMIT) : workRows
  const topSignIns =
    searchTerm === "" ? signInRows.slice(0, SIDE_LIST_LIMIT) : signInRows

  return (
    <section
      className={cn("flex min-h-0 flex-col gap-4", className)}
      {...sectionProps}
    >
      <PortfolioSectionHeader
        eyebrow="Geography"
        title="Geography"
        lead="The municipalities the customers' work is about, and the places their people sign in from."
        info="Green: municipalities the tracked work concentrates in (each user's top areas, always the last 28 days) — where to anchor market reports and prospecting. Teal: where users sign in from (geoip, follows the timeframe selector); foreign or unmatched cities appear in the side list only. Circle area tracks volume."
      >
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <GeoLayerToggle
            label="Work areas"
            color={GEO_WORK_COLOR}
            isActive={showWork}
            onToggle={() => setShowWork(!showWork)}
          />
          <GeoLayerToggle
            label="Sign-ins"
            color={GEO_SIGNIN_COLOR}
            isActive={showSignIns}
            onToggle={() => setShowSignIns(!showSignIns)}
          />
          {/* Re-windows the sign-in layer only — the work areas come from
              the fixed 28d profiles, as the list label says. */}
          <PortfolioTimeframeSelect
            value={timeframe.key}
            onChange={timeframe.setKey}
          />
        </div>
      </PortfolioSectionHeader>

      <ErrorMessage error={error} />

      {/* The map takes every vertical pixel the tab has; the side lists get
          their own scroll instead of stretching the page. */}
      <div className="grid min-h-0 flex-1 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
        <Suspense
          fallback={
            <div className="flex min-h-[420px] items-center justify-center rounded-md border text-xs text-muted-foreground">
              Loading map…
            </div>
          }
        >
          <PortfolioGeoMap
            className="min-h-[420px] xl:h-full"
            workPlaces={workPlaces}
            signInPlaces={signInPlaces}
            showWork={showWork}
            showSignIns={showSignIns}
          />
        </Suspense>

        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          <Input
            type="search"
            size="xs"
            placeholder="Search places…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex min-h-0 flex-col gap-3 overflow-y-auto pr-1">
            <GeoTopList
              title="Where the work happens"
              unit="located work actions · last 28 d"
              color={GEO_WORK_COLOR}
              emptyText={
                loading
                  ? "Loading…"
                  : searchTerm !== ""
                    ? "No places match the search."
                    : "No located usage in the last 28 days."
              }
              rows={topWork}
            />
            <GeoTopList
              title="Sign-in locations"
              unit="sign-ins · selected timeframe"
              color={GEO_SIGNIN_COLOR}
              emptyText={
                loading
                  ? "Loading…"
                  : searchTerm !== ""
                    ? "No places match the search."
                    : "No geolocated sign-ins in the window yet."
              }
              rows={topSignIns}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

type GeoLayerToggleProps = {
  label: string
  color: string
  isActive: boolean
  onToggle(): void
}

function GeoLayerToggle(props: GeoLayerToggleProps) {
  const { label, color, isActive, onToggle } = props
  return (
    <button
      type="button"
      aria-pressed={isActive}
      className={cn(
        "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
        isActive
          ? "border-border bg-muted/50"
          : "border-transparent text-muted-foreground hover:bg-muted/30",
      )}
      onClick={onToggle}
    >
      <span
        className={cn("size-2 rounded-full", !isActive && "opacity-30")}
        style={{ backgroundColor: color }}
      />
      {label}
    </button>
  )
}

type GeoTopListProps = {
  title: string
  /** What the numbers count — no bare numbers anywhere. */
  unit: string
  color: string
  emptyText: string
  rows: { key: string; label: string; value: number }[]
}

function GeoTopList(props: GeoTopListProps) {
  const { title, unit, color, emptyText, rows } = props
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-baseline gap-1.5 text-xs font-medium text-muted-foreground">
        <span
          className="size-2 shrink-0 self-center rounded-full"
          style={{ backgroundColor: color }}
        />
        {title}
        <span className="min-w-0 truncate font-normal opacity-70">
          · {unit}
        </span>
      </span>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <span className="min-w-0 truncate">{row.label}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatNumber(row.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default PortfolioGeoSection
